import { useRef } from 'react';
import { HotTable, type HotTableRef } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import type Handsontable from 'handsontable';
import type { ColumnConditions } from 'handsontable/plugins/filters';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import { useHandsontableLanguage } from '@/hooks/useHandsontableLanguage';
import {
  applyDraftPriceChange,
  type CsvTableDraft,
} from './pricelistCsvEditState';
import {
  csvCellText,
  csvCellEditsFromChanges,
  type CsvCellEdit,
} from './pricelistCsvGridChanges';
import {
  GRID_ROW_HEIGHT,
  useSpreadsheetGridHeight,
} from './spreadsheetGridViewport';

registerAllModules();

/**
 * The grid's column filters, held by the caller: the grid reloads (and so
 * forgets its own filters) on every save, discard and table switch.
 */
export interface CsvGridColumnFilters {
  /** Re-applied whenever the grid loads its rows. */
  conditions: ColumnConditions[];
  onChange: (conditions: ColumnConditions[]) => void;
}

function applyColumnFilters(
  hot: Handsontable | undefined,
  conditions: ColumnConditions[],
) {
  if (!hot || conditions.length === 0) return;
  const filters = hot.getPlugin('filters');
  for (const {
    column,
    operation,
    conditions: columnConditions,
  } of conditions) {
    for (const { name, args } of columnConditions) {
      if (name) filters.addCondition(column, name, args, operation);
    }
  }
  filters.filter();
}

/**
 * One extracted table as a spreadsheet: cells are typed into directly, and
 * multi-cell ranges copy/paste between cells (and to and from Excel or Google
 * Sheets) through Handsontable's CopyPaste plugin. Column headers come from the
 * extraction and aren't editable.
 *
 * Handsontable owns `draft.rows` and mutates it in place, so the array identity
 * must stay stable across renders — `dataVersion` is the only thing that makes
 * it reload (discard, or a fresh fetch), which keeps the selection intact while
 * typing.
 *
 * With `readOnly`, cells still select and copy out (the comparison panel's whole
 * point) but typing and pasting in are rejected by Handsontable itself.
 *
 * With `columnFilters`, every column header gets an Excel-style menu: filter by
 * value (check or uncheck values to include or exclude rows) or by condition.
 * Filtering only hides rows — `draft.rows` keeps them all, so a save still
 * posts the whole table — and row headers keep each row's table number.
 */
export function PricelistCsvGrid({
  draft,
  dataVersion,
  onCellsEdited,
  readOnly = false,
  columnFilters,
  rowLabels,
  maxHeight,
}: {
  draft: CsvTableDraft;
  dataVersion: number;
  onCellsEdited?: (edits: CsvCellEdit[]) => void;
  readOnly?: boolean;
  /** Per-column filter menus in the headers; view-only, nothing is saved. */
  columnFilters?: CsvGridColumnFilters;
  /**
   * Caps the grid. A long table scrolls inside this height instead of
   * stretching the page under it.
   */
  maxHeight?: number;
  /**
   * What each row's header cell says, when its position in `draft.rows` is not
   * its position in the table — search results, which are rows lifted out of a
   * table. Left off, rows number 1..N, which is right for a whole table or a
   * page of one but would misreport a filtered set.
   */
  rowLabels?: (index: number) => string;
}) {
  const hotRef = useRef<HotTableRef>(null);
  const language = useHandsontableLanguage();
  const [wrapperRef, gridHeight] = useSpreadsheetGridHeight(
    draft.rows.length,
    maxHeight,
  );

  /**
   * Caps + fold + strip before the value lands, so a pasted vendor description
   * shows the AS/400 spelling. Extraction already did this; this covers edits
   * of older files and anything typed after extract. See sanitizeAs400Text.
   */
  function handleBeforeChange(
    changes: Array<Handsontable.CellChange | null>,
    source: Handsontable.ChangeSource,
  ) {
    if (readOnly || source === 'loadData' || source === 'updateData') {
      return;
    }
    for (const change of changes) {
      if (!change) continue;
      const [, prop, , newValue] = change;
      if (typeof prop !== 'string') continue;
      change[3] = applyDraftPriceChange(draft, prop, csvCellText(newValue));
    }
  }

  function handleAfterChange(
    changes: Handsontable.CellChange[] | null,
    source: string,
  ) {
    // `loadData` fires when the data array itself is swapped — nothing typed.
    if (!changes || source === 'loadData') return;
    const hot = hotRef.current?.hotInstance;
    // No instance means no way to tell a filtered row's place, so nothing is
    // reported rather than an edit keyed to the wrong row.
    if (!hot) return;
    const edits = csvCellEditsFromChanges(changes, (visualRow) =>
      hot.toPhysicalRow(visualRow),
    );
    if (edits.length > 0) onCellsEdited?.(edits);
  }

  return (
    <div ref={wrapperRef} className="overflow-hidden rounded-md border">
      <HotTable
        ref={hotRef}
        key={dataVersion}
        data={draft.rows}
        columns={draft.headers.map((header) => ({ data: header }))}
        colHeaders={draft.headers}
        rowHeaders={rowLabels ?? true}
        width="100%"
        height={gridHeight}
        colWidths={140}
        readOnly={readOnly}
        licenseKey="non-commercial-and-evaluation"
        language={language}
        afterChange={handleAfterChange}
        outsideClickDeselects={false}
        contextMenu={false}
        filters={columnFilters !== undefined}
        dropdownMenu={
          columnFilters
            ? ['filter_by_condition', 'filter_by_value', 'filter_action_bar']
            : false
        }
        bindRowsWithHeaders={columnFilters !== undefined}
        afterInit={() =>
          applyColumnFilters(
            hotRef.current?.hotInstance ?? undefined,
            columnFilters?.conditions ?? [],
          )
        }
        afterFilter={(conditions) => columnFilters?.onChange(conditions)}
        manualColumnResize
        autoColumnSize={false}
        autoRowSize={false}
        rowHeights={GRID_ROW_HEIGHT}
        beforeChange={handleBeforeChange}
        enterBeginsEditing
        enterMoves={{ row: 1, col: 0 }}
        tabMoves={{ row: 0, col: 1 }}
      />
    </div>
  );
}
